import { useEffect } from "react";

interface KeyboardShortcutOptions {
    key: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
    preventDefault?: boolean;
    enabled?: boolean;
}

export function useKeyboardShortcut(callback: (event: KeyboardEvent) => void, options: KeyboardShortcutOptions) {
    const { key, ctrlKey = false, metaKey = false, shiftKey = false, altKey = false, preventDefault = true, enabled = true } = options;

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            // Ignorer si on est dans un input, textarea ou contenteditable
            const target = event.target as HTMLElement;
            const isInputElement = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.contentEditable === "true";

            // Exception : permettre CMD+K même dans les inputs (sauf si c'est déjà la SearchBox)
            const isSearchException = key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey) && !target.closest("[data-search-box]");

            if (isInputElement && !isSearchException) return;

            // Vérifier si la combinaison de touches correspond
            const keyMatches = event.key.toLowerCase() === key.toLowerCase();
            const ctrlMatches = ctrlKey === event.ctrlKey;
            const metaMatches = metaKey === event.metaKey;
            const shiftMatches = shiftKey === event.shiftKey;
            const altMatches = altKey === event.altKey;

            if (keyMatches && ctrlMatches && metaMatches && shiftMatches && altMatches) {
                if (preventDefault) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                callback(event);
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [callback, key, ctrlKey, metaKey, shiftKey, altKey, preventDefault, enabled]);
}

// Hook universel pour CMD+K/Ctrl+K (détecte automatiquement la plateforme)
export function useSearchShortcut(callback: () => void, enabled = true) {
    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            // Vérifier si la touche est définie
            if (!event.key) return;
            
            // Vérifier si c'est CMD+K (Mac) ou Ctrl+K (Windows/Linux)
            const isSearchShortcut = event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey;
            if (!isSearchShortcut) return;

            // Ignorer si on est dans un input, textarea ou contenteditable
            const target = event.target as HTMLElement;
            const isInputElement = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.contentEditable === "true";

            // Exception : permettre CMD+K même dans les inputs (sauf si c'est déjà la SearchBox)
            const isSearchBox = target.closest("[data-search-box]");

            if (isInputElement && !isSearchBox) return;

            event.preventDefault();
            event.stopPropagation();
            callback();
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [callback, enabled]);
}

// Hook spécialisé pour CMD+K / Ctrl+K (obsolète, remplacé par useSearchShortcut)
export function useCommandK(callback: () => void, enabled = true) {
    return useKeyboardShortcut(callback, {
        key: "k",
        metaKey: true,
        ctrlKey: false,
        enabled
    });
}

// Hook pour Windows/Linux Ctrl+K (obsolète, remplacé par useSearchShortcut)
export function useControlK(callback: () => void, enabled = true) {
    return useKeyboardShortcut(callback, {
        key: "k",
        ctrlKey: true,
        metaKey: false,
        enabled
    });
}
